import { customizationsNPCPath } from '@/app/constants';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { EquipmentConfiguration } from '@/common/types/student';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { TilemapLevels } from '@/game/GameComponents/PhaserGame/main';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { CameraController } from '@/game/GameComponents/PhaserGame/Utils/CameraController';
import {
  getNpcCustomizationFromId,
  getSpriteSheetPath,
} from '@/game/GameComponents/PhaserGame/Utils/CosmeticUtils';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { createNPCOutfit } from '@/game/GameComponents/PhaserGame/Utils/NpcUtils';
import { getNodeStatus } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import Mentor from '@/game/prefabs/World/Mentors/Mentor';
import Player from '@/game/prefabs/World/Player';
import Portal from '@/game/prefabs/World/Portal';
import WorldNode from '@/game/prefabs/World/WorldNode';

export default class TilemapScene extends Phaser.Scene {
  public playerRef: Player | null = null;
  public tilemapRef: Phaser.Tilemaps.Tilemap | null = null;
  public portalRef: Portal | null = null;
  public mentorRef: Mentor | null = null;

  nodes: WorldNode[] = [];
  cameraController: CameraController | null = null;

  preload() {
    const key = this.scene.key;
    const biome = TilemapLevels.find((l) => l.key === key)?.biome;
    if (!biome) {
      console.error('couldnt find biome for level: ', key);
      throw new Error(`couldnt find biome for level: ${key}`);
    }
    // Check if the pack JSON has been loaded
    if (!this.cache.json.exists(`tiles-${biome}`)) {
      this.load.pack(
        `tiles-${biome}`,
        `game_assets/tilemaps/tiles-${biome}.json`,
      );
    }
  }

  initialize(data: any) {
    this.playerRef = data.player;
    this.tilemapRef = data.tilemap;
    this.portalRef = data.portal;
    this.mentorRef = data.mentor;

    // Initialize camera.
    this.cameraController = new CameraController({
      scene: this,
      tilemap: this.tilemapRef,
      player: this.playerRef,
      camera: data.camera,
    }).initialize();

    // Position the player.
    const gameInstance = GameInstance.instance;
    if (gameInstance.generalGameState.lastNodeIndex !== -1) {
      const targetPos = new Phaser.Math.Vector2(
        gameInstance.gameMap.playerPosition.x,
        gameInstance.gameMap.playerPosition.y,
      );

      if (
        targetPos.x < 0 ||
        targetPos.y < 0 ||
        targetPos.x > this.tilemapRef!.width ||
        targetPos.y > this.tilemapRef!.height
      ) {
        // We tried to position the player outside of the world. Reset their position.
        targetPos.set(0, 0);
      }
      this.playerRef!.body!.position = targetPos;
    }

    EventBus.emit(GAME_EVENTS.TILEMAP_LEVEL_INITIALIZED, {
      tilemapScene: this,
      player: this.playerRef,
      tilemap: this.tilemapRef,
      portal: this.portalRef,
      mentor: this.mentorRef,
      camera: this.cameras.main,
    });

    // Preload NPC cosmetics for nodes.
    const configurations: Partial<EquipmentConfiguration>[] = [];
    for (let n in this.nodes) {
      const node = this.nodes[n];
      if (!node.visible) continue;
      const configuration = createNPCOutfit(
        getStudent().id!,
        node.nPC.npcLevelId,
        node.nPC.npcId,
      );
      configurations[n] = configuration;
      Object.keys(configuration).forEach((c: string) => {
        if (c === undefined || c === null) return;
        const key = c as keyof EquipmentConfiguration;
        if (key === undefined || key === null) return;
        const customizationId = configuration[key];
        if (customizationId === undefined || customizationId === null) return;
        const customization = getNpcCustomizationFromId(customizationId);
        if (customization === undefined || customization === null) return;
        const spriteSheetPath = getSpriteSheetPath(customization);

        if (this.textures.exists(spriteSheetPath)) return;
        this.load.spritesheet(
          spriteSheetPath,
          `${customizationsNPCPath}${customization.slot}/${spriteSheetPath}.png`,
          { frameWidth: 64, frameHeight: 64 },
        );
      });
    }

    this.load.once('complete', () => {
      for (var n = 0; n < this.nodes.length; n++) {
        const node = this.nodes[n];

        if (node.visible) {
          node.nPC.configure(configurations[n]);
          this.playerRef?.createAnimations();
          node.setStatus(node.npcStatus!);
        }
        // Create animations for the npc.
        node.nPC.createAnimations();
      }

      this.updateNodes();
      EventBus.emit(GAME_EVENTS.FINISHED_NPC_ASSET_PRELOAD);
    });
    this.load.start();

    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.NODE_COMPLETED,
      this.updateNodes,
      this,
    );
    this.events.once('shutdown', this.handleShutdown, this);
  }

  // Ensures the correct number of nodes are present in this scene for the current s+s level.
  updateNodes() {
    const sLevel = getStudent().level;
    const level = scopeAndSequence.levels.find((l) => l.level === sLevel);
    if (!level) {
      console.warn('couldnt find node data for level: ', sLevel);
      return;
    }
    const customLevel = GameInstance.instance.customLevel;
    const playCustomLevel =
      customLevel &&
      GameInstance.instance.generalGameState.optedIntoCustomLevel;

    const numGames = playCustomLevel
      ? Object.values(customLevel).filter(
          (v) => v && typeof v === 'object' && 'enabled' in v && v.enabled,
        ).length
      : level.games.length;

    for (let i = 0; i < this.nodes.length; i++) {
      const node = this.nodes[i];
      node.setVisible(i < numGames);
      if (node.visible) {
        node.setStatus(getNodeStatus(i));
      }
    }
  }

  handleShutdown() {
    this.nodes = [];
    this.playerRef = null;

    this.tilemapRef?.destroy();
    this.tilemapRef = null;

    this.cameraController = null;
    EventBus.off(GAME_EVENTS.NODE_COMPLETED, this.updateNodes, this);
  }
}
